import BottomSheetContent from '../../../../../components/BottomSheetComponents/BottomSheetContent'
import { PrimaryButton } from '../../../../../components/execution/PrimaryButton'
import { TertiaryButton } from '../../../../../components/execution/TertiaryButton'
import RenderHTML from '../../../../../components/ui-components/RenderHTML'
import { useBottomSheet } from '../../../../../state-management/contexts/BottomSheetContext'
import { useToast } from '../../../../../state-management/contexts/ToastContext'
import {
  ExecutionLearnMoreReason,
  OnboardingStatus,
  SubActionDisabledReason
} from '../../../../../types/mf-execution.types'
import { lightImpactVibrate, postCTA, trackEvent } from '../../../../../utils/appBridge'
import { SUPPORT_CTA } from '../../../../../utils/commonAppCTAs'
import { LumpsumRecommendationActionType } from '../../lumpsum/api/common.types'
import {
  postPlanCta,
  toastFundExecutionError,
  useFetchFundExecutionWithLoading,
  type PlanFundActionHookOptions
} from '../fundExecutionHelpers'
import { RegularFundNotSupportedBottomSheet } from '../RegularFundNotSupportedBottomSheet'
import { EventNames } from '../../../../../types/misc.types'
import { PlansExecutionEventIds, PlansScreen } from '../../analytics/plansEvents'
import { usePpfasGiftFundHandling } from '../../../../../features/ppfas-handling/usePpfasGiftFundHandling'

export type PlanBuyActionInput = {
  isin: string
  amount: number
  fundName?: string
}

export function usePlanBuyAction({
  transactionCta,
  isRecommendationSheetSource = false,
  analyticsScreen = PlansScreen.LumpsumAllocationRecommendation
}: PlanFundActionHookOptions) {
  const { showToast } = useToast()
  const {
    openBottomSheet,
    closeBottomSheet,
    openNestedBottomSheet,
    closeNestedBottomSheet
  } = useBottomSheet()
  const openErrorSheet = isRecommendationSheetSource ? openNestedBottomSheet : openBottomSheet
  const closeErrorSheet = isRecommendationSheetSource ? closeNestedBottomSheet : closeBottomSheet
  const fetchFundExecution = useFetchFundExecutionWithLoading()
  const { openPpfasGiftFund } = usePpfasGiftFundHandling({ isRecommendationSheetSource })

  async function onBuy({ isin, amount, fundName = '' }: PlanBuyActionInput) {
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: analyticsScreen,
        eventId: PlansExecutionEventIds.Buy,
        buttonLabel: 'Buy',
        ...(fundName ? { fundName } : {}),
        ...(isin ? { fundIsin: isin } : {})
      }
    })
    const openedPpfasGiftFund = await openPpfasGiftFund({ isin, analyticsScreen })
    if (openedPpfasGiftFund) {
      return
    }
    const data = await fetchFundExecution(isin)
    if (!data) {
      toastFundExecutionError(showToast)
      return
    }

    if (data?.executionOnboardingStatus === OnboardingStatus.NOT_ONBOARDED) {
      postPlanCta(data?.cta, showToast)
      return
    }

    const isRegularPlan =
      data?.buttonDisabledStates?.learnMoreReason === ExecutionLearnMoreReason.REGULAR ||
      data?.buttonDisabledStates?.buy?.disabledReason === SubActionDisabledReason.REGULAR

    if (isRegularPlan) {
      openErrorSheet(
        <RegularFundNotSupportedBottomSheet
          onDismiss={closeErrorSheet}
          analyticsScreen={analyticsScreen}
          fundName={fundName}
          fundIsin={isin}
        />,
        closeErrorSheet
      )
      return
    }

    if (data?.buttonDisabledStates?.buy?.isDisabled) {
      openErrorSheet(
        <FundBuyNotAllowedBottomSheet
          onDismiss={closeErrorSheet}
          analyticsScreen={analyticsScreen}
          fundName={fundName}
          fundIsin={isin}
        />,
        closeErrorSheet
      )
      return
    }

    if (transactionCta == null || typeof transactionCta !== 'object') {
      postPlanCta(null, showToast)
      return
    }

    const inputData = {
      isin,
      amount: String(amount),
      recommendationAction: LumpsumRecommendationActionType.BUY
    }
    const filledCta = {
      ...(transactionCta as Record<string, unknown>),
      requestPayload: {
        inputData
      }
    }
    console.log('[plans-v4] filled execution CTA', filledCta)
    postPlanCta(filledCta, showToast)
  }

  return { onBuy }
}

export function FundBuyNotAllowedBottomSheet({
  onDismiss,
  analyticsScreen = PlansScreen.LumpsumAllocation,
  fundName = '',
  fundIsin = ''
}: {
  onDismiss: () => void
  analyticsScreen?: string
  fundName?: string
  fundIsin?: string
}) {
  return (
    <BottomSheetContent
      logoSvg={<RenderHTML innerHTMLText={FUND_ACTION_SLEEP_ICON_SVG} className="size-10" />}
      heading="This fund has stopped taking new lump-sum investments"
      bottomSheetHeadingClassName="!font-[500] !leading-[130%] !text-[22px]"
      onDismiss={onDismiss}
      bottomSheetCtas={
        <div className="flex w-full flex-col gap-3">
          <TertiaryButton
            testLocator="BS_CTA_SUPPORT"
            label="Contact Support"
            tertiaryButtonClassName="!h-[56px] text-[16px]"
            onClick={() => {
              lightImpactVibrate()
              trackEvent({
                eventName: EventNames.ButtonTap,
                payload: {
                  ScreenName: analyticsScreen,
                  eventId: PlansExecutionEventIds.NotAllowedSupport,
                  buttonLabel: 'Contact Support',
                  ...(fundName ? { fundName } : {}),
                  ...(fundIsin ? { fundIsin } : {})
                }
              })
              postCTA(JSON.stringify(SUPPORT_CTA))
            }}
          />
          <PrimaryButton
            testLocator="BS_CTA_PRIMARY"
            label="Close"
            primaryButtonClassName="!h-[56px] text-[16px]"
            onClick={() => {
              trackEvent({
                eventName: EventNames.ButtonTap,
                payload: {
                  ScreenName: analyticsScreen,
                  eventId: PlansExecutionEventIds.NotAllowedClose,
                  buttonLabel: 'Close',
                  ...(fundName ? { fundName } : {}),
                  ...(fundIsin ? { fundIsin } : {})
                }
              })
              onDismiss()
            }}
          />
        </div>
      }
    >
      <div className="pb-2 text-[14px] font-[400] leading-[150%] text-[#6A6E6E]">
        Please do not worry, our advisor will review your plan and provide you an alternative advisory.
      </div>
    </BottomSheetContent>
  )
}

const FUND_ACTION_SLEEP_ICON_SVG = `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="18" cy="22" r="11" stroke="#232323" stroke-width="1.6"/><path d="M18 17v5l3.5 2" stroke="#232323" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M26 8h4.5l-4 5H31" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M30.5 4h3l-2.5 3.5H34" stroke="#232323" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>`
